/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BulkSocialPostsPathParameters,
  BulkSocialPostsRequest,
  ClaimSocialWebsitePathParameters,
  ConnectSocialRequest,
  CreateSocialPostPathParameters,
  CreateSocialPostRequest,
  DisableSocialAdsPathParameters,
  DisableSocialCatalogPathParameters,
  DisconnectSocialPathParameters,
  EnableSocialAdsPathParameters,
  EnableSocialAdsRequest,
  EnableSocialCatalogPathParameters,
  GetSocialAnalyticsOverviewParams,
  GetSocialAnalyticsOverviewPathParameters,
  GetSocialCatalogPathParameters,
  GetSocialPostPathParameters,
  IngestSocialCatalogPathParameters,
  ListSocialAdAccountsPathParameters,
  ListSocialCampaignAnalyticsParams,
  ListSocialCampaignAnalyticsPathParameters,
  ListSocialConnectionsPathParameters,
  ListSocialPostsParams,
  ListSocialPostsPathParameters,
  ListSocialTargetsPathParameters,
  PublishSocialPostPathParameters,
  PublishSocialPostRequest,
  RemoveSocialPostParams,
  RemoveSocialPostPathParameters,
  SocialAdAccountList,
  SocialAnalyticsOverview,
  SocialAuthorizeUrl,
  SocialBulkResult,
  SocialCampaignList,
  SocialCatalogStatus,
  SocialConnection,
  SocialConnectionList,
  SocialPost,
  SocialPostList,
  SocialTargetList,
  StartSocialAdsConnectPathParameters,
  StartSocialConnectPathParameters,
  UnscheduleSocialPostPathParameters,
  UpdateSocialPostPathParameters,
  UpdateSocialPostRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * One row per network the store has connected, including those since disconnected (`revoked`) or whose refresh failed (`expired`). Tokens are never returned
 * @summary The store's network connections
 */
export const listSocialConnections = (
    { storeId }: ListSocialConnectionsPathParameters,
 options?: SecondParameter<typeof customInstance<SocialConnectionList>>,) => {
      return customInstance<SocialConnectionList>(
      {url: `/admin/v1/stores/${storeId}/social/connections`, method: 'GET'
    },
      options);
    }
  /**
 * Answers the network's authorization URL with a signed ten-minute state naming the store and the member. `purpose: ads` asks for the ads scopes on top of the connect scopes (the ads step). The network returns the browser to the API's callback, which lands on the store's Channels page with `?connected=<provider>` or `?error=` (cancelled, business_required, provider_rejected, rate_limited). Connecting again replaces the tokens and scopes and keeps posts
 * @summary Where to send the browser to connect the network account
 */
export const startSocialConnect = (
    { storeId, provider }: StartSocialConnectPathParameters,
    connectSocialRequestNull: BodyType<ConnectSocialRequest | null>,
 options?: SecondParameter<typeof customInstance<SocialAuthorizeUrl>>,) => {
      return customInstance<SocialAuthorizeUrl>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/connect`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: connectSocialRequestNull
    },
      options);
    }
  /**
 * Revokes the tokens on the network (best effort), drops them here, switches catalogue sync and the ads account off and keeps the row as `revoked` so posts keep their history. 404 when the network is not connected
 * @summary Disconnect the network account
 */
export const disconnectSocial = (
    { storeId, provider }: DisconnectSocialPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/connection`, method: 'DELETE'
    },
      options);
    }
  /**
 * Read live from the network. `{ enabled: false }` with empty details while sync is off or the store is not connected. `utm` says whether feed links carry utm_source and utm_medium=catalog while sync is on
 * @summary Catalogue sync state: feed registration, last processing run and its first item issues
 */
export const getSocialCatalog = (
    { storeId, provider }: GetSocialCatalogPathParameters,
 options?: SecondParameter<typeof customInstance<SocialCatalogStatus>>,) => {
      return customInstance<SocialCatalogStatus>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/catalog`, method: 'GET'
    },
      options);
    }
  /**
 * Mints the channel's feed token when the store has none. The network fetches the feed daily at settings.social.<provider>.catalogProcessingTime in the store timezone. Calling it again while sync is on returns the current state. 409 SOCIAL_NOT_CONNECTED without a connection, SOCIAL_CONNECTION_EXPIRED when the account must be reconnected
 * @summary Turn catalogue sync on: register the store's feed URL with the network and ask for a first fetch
 */
export const enableSocialCatalog = (
    { storeId, provider }: EnableSocialCatalogPathParameters,
 options?: SecondParameter<typeof customInstance<SocialCatalogStatus>>,) => {
      return customInstance<SocialCatalogStatus>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/catalog/enable`, method: 'POST'
    },
      options);
    }
  /**
 * The store's feed URL and token stay valid for other channels. Calling it while sync is already off returns `{ enabled: false }`
 * @summary Turn catalogue sync off: delete the feed on the network and stop tagging feed links
 */
export const disableSocialCatalog = (
    { storeId, provider }: DisableSocialCatalogPathParameters,
 options?: SecondParameter<typeof customInstance<SocialCatalogStatus>>,) => {
      return customInstance<SocialCatalogStatus>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/catalog/disable`, method: 'POST'
    },
      options);
    }
  /**
 * Answers the state after the request; processing is asynchronous on the network side, so `lastProcessing` reflects the previous run until it finishes. 409 CONFLICT while sync is off
 * @summary Sync now: ask the network to fetch the feed ahead of its daily schedule
 */
export const ingestSocialCatalog = (
    { storeId, provider }: IngestSocialCatalogPathParameters,
 options?: SecondParameter<typeof customInstance<SocialCatalogStatus>>,) => {
      return customInstance<SocialCatalogStatus>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/catalog/ingest`, method: 'POST'
    },
      options);
    }
  /**
 * Answers the network's authorisation URL. Same callback as the account connection; on return the connection carries the ads scopes and GET ad-accounts lists what the merchant can pick. Needs an existing account connection
 * @summary Start the ads consent: the account's connect flow with ads:read and ads:write on top
 */
export const startSocialAdsConnect = (
    { storeId, provider }: StartSocialAdsConnectPathParameters,
 options?: SecondParameter<typeof customInstance<SocialAuthorizeUrl>>,) => {
      return customInstance<SocialAuthorizeUrl>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/ads/connect`, method: 'POST'
    },
      options);
    }
  /**
 * 409 SOCIAL_ADS_NOT_CONNECTED until the connection carries ads:read (run the ads consent first)
 * @summary Ad accounts the connected account can read, live from the network
 */
export const listSocialAdAccounts = (
    { storeId, provider }: ListSocialAdAccountsPathParameters,
 options?: SecondParameter<typeof customInstance<SocialAdAccountList>>,) => {
      return customInstance<SocialAdAccountList>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/ad-accounts`, method: 'GET'
    },
      options);
    }
  /**
 * The token is stored encrypted and never returned. The store's pinterest_capi destination is created or updated with the account id; the forwarder then sends server-side purchases with the connection's token. Nightly campaign snapshots start from the next run. 409 SOCIAL_ADS_NOT_CONNECTED without ads:read and ads:write, 422 INVALID_REFERENCE for an account the connection cannot use
 * @summary Pick the ad account: mints the Conversions API token and connects the pinterest_capi analytics destination
 */
export const enableSocialAds = (
    { storeId, provider }: EnableSocialAdsPathParameters,
    enableSocialAdsRequest: BodyType<EnableSocialAdsRequest>,
 options?: SecondParameter<typeof customInstance<SocialConnection>>,) => {
      return customInstance<SocialConnection>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/ads/enable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: enableSocialAdsRequest
    },
      options);
    }
  /**
 * The conversion token is dropped and campaign snapshots stop; the scopes stay on the connection so enabling again needs no new consent. A no-op when ads is already off
 * @summary Forget the ad account and remove the pinterest_capi destination
 */
export const disableSocialAds = (
    { storeId, provider }: DisableSocialAdsPathParameters,
 options?: SecondParameter<typeof customInstance<SocialConnection>>,) => {
      return customInstance<SocialConnection>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/ads/disable`, method: 'POST'
    },
      options);
    }
  /**
 * Impressions and outbound clicks come from the nightly account snapshots, ad spend from the campaign snapshots (null until an ad account is connected), revenue from the store's own orders whose checkout session carried utm_source=<provider> (organic = utm_medium social or catalog, paid = utm_medium paid). Days are the store's calendar days, inclusive, at most 366. Answers 200 with connected=false and zero tiles when the network is not connected; hasData is false until the first snapshot has run
 * @summary The social analytics page: four tiles with previous-period values, one daily series and the top products
 */
export const getSocialAnalyticsOverview = (
    { storeId }: GetSocialAnalyticsOverviewPathParameters,
    params: GetSocialAnalyticsOverviewParams,
 options?: SecondParameter<typeof customInstance<SocialAnalyticsOverview>>,) => {
      return customInstance<SocialAnalyticsOverview>(
      {url: `/admin/v1/stores/${storeId}/social/analytics/overview`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Spend, impressions, clicks and the network-reported conversions and checkout value per campaign, in the store currency, highest spend first. Empty until an ad account is connected and the first snapshot has run
 * @summary Per-campaign totals over the range from the nightly campaign snapshots
 */
export const listSocialCampaignAnalytics = (
    { storeId }: ListSocialCampaignAnalyticsPathParameters,
    params: ListSocialCampaignAnalyticsParams,
 options?: SecondParameter<typeof customInstance<SocialCampaignList>>,) => {
      return customInstance<SocialCampaignList>(
      {url: `/admin/v1/stores/${storeId}/social/analytics/campaigns`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Pinterest: the account's boards with their sections. Nothing is cached; 409 SOCIAL_NOT_CONNECTED without a live connection, 429 SOCIAL_RATE_LIMITED when the network throttles
 * @summary Where a post can land, read live from the network
 */
export const listSocialTargets = (
    { storeId, provider }: ListSocialTargetsPathParameters,
 options?: SecondParameter<typeof customInstance<SocialTargetList>>,) => {
      return customInstance<SocialTargetList>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/targets`, method: 'GET'
    },
      options);
    }
  /**
 * Every status including removed; filter by `provider`, `status` (comma list) and `q` on title, description and link
 * @summary The store's posts, newest first
 */
export const listSocialPosts = (
    { storeId }: ListSocialPostsPathParameters,
    params?: ListSocialPostsParams,
 options?: SecondParameter<typeof customInstance<SocialPostList>>,) => {
      return customInstance<SocialPostList>(
      {url: `/admin/v1/stores/${storeId}/social/posts`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Needs a live connection for the network. `prefill: true` fills title, description, alt text and link from the first product where the field was not sent; the link is the product page on the primary domain, with `?variant=` when a variant was picked. The stored link stays bare: the UTM parameters are added when the post goes out. Saving enqueues the network rendition of every image (Pinterest 2:3 JPEG); publish once `media[].url` is set
 * @summary Save a draft post from products and images
 */
export const createSocialPost = (
    { storeId }: CreateSocialPostPathParameters,
    createSocialPostRequest: BodyType<CreateSocialPostRequest>,
 options?: SecondParameter<typeof customInstance<SocialPost>>,) => {
      return customInstance<SocialPost>(
      {url: `/admin/v1/stores/${storeId}/social/posts`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createSocialPostRequest
    },
      options);
    }
  /**
 * @summary One post
 */
export const getSocialPost = (
    { storeId, id }: GetSocialPostPathParameters,
 options?: SecondParameter<typeof customInstance<SocialPost>>,) => {
      return customInstance<SocialPost>(
      {url: `/admin/v1/stores/${storeId}/social/posts/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * A new image list resets the renditions and renders again; a scheduled post keeps its time. 409 SOCIAL_POST_LOCKED while publishing, once published and after removal; 409 SOCIAL_POST_DUPLICATE when a scheduled post would now match another live post on the same board
 * @summary Change a draft, scheduled or failed post
 */
export const updateSocialPost = (
    { storeId, id }: UpdateSocialPostPathParameters,
    updateSocialPostRequest: BodyType<UpdateSocialPostRequest>,
 options?: SecondParameter<typeof customInstance<SocialPost>>,) => {
      return customInstance<SocialPost>(
      {url: `/admin/v1/stores/${storeId}/social/posts/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateSocialPostRequest
    },
      options);
    }
  /**
 * The row stays as `removed`. `removeFromProvider=true` also deletes a published post on the network (a post the network no longer has counts as deleted). 409 while the post is being published; removing twice is a no-op
 * @summary Remove a post
 */
export const removeSocialPost = (
    { storeId, id }: RemoveSocialPostPathParameters,
    params?: RemoveSocialPostParams,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/social/posts/${id}`, method: 'DELETE',
        params
    },
      options);
    }
  /**
 * Marks the post `scheduled` for the minute sweep, with `publishAt` now (omitted or past) or the given time. Needs a board and rendered images (409 SOCIAL_POST_NOT_PUBLISHABLE, details name what is missing), a live connection (409 SOCIAL_NOT_CONNECTED), a free slot under the day's cap in the store timezone (422 LIMIT_EXCEEDED with meta.limit and meta.day) and no live post with the same images and link on the same board (409 SOCIAL_POST_DUPLICATE). From failed this is the retry; from scheduled it is a new time
 * @summary Publish now or at a time
 */
export const publishSocialPost = (
    { storeId, id }: PublishSocialPostPathParameters,
    publishSocialPostRequestNull: BodyType<PublishSocialPostRequest | null>,
 options?: SecondParameter<typeof customInstance<SocialPost>>,) => {
      return customInstance<SocialPost>(
      {url: `/admin/v1/stores/${storeId}/social/posts/${id}/publish`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: publishSocialPostRequestNull
    },
      options);
    }
  /**
 * @summary Take a scheduled post back to draft
 */
export const unscheduleSocialPost = (
    { storeId, id }: UnscheduleSocialPostPathParameters,
 options?: SecondParameter<typeof customInstance<SocialPost>>,) => {
      return customInstance<SocialPost>(
      {url: `/admin/v1/stores/${storeId}/social/posts/${id}/unschedule`, method: 'POST'
    },
      options);
    }
  /**
 * One outcome per id, in order: `ok` with the status after the action, or the error code and message the single-post route would have answered (`status` null when the post was not found). The request itself answers 200 whatever the outcomes
 * @summary Publish now, schedule or discard several posts
 */
export const bulkSocialPosts = (
    { storeId }: BulkSocialPostsPathParameters,
    bulkSocialPostsRequest: BodyType<BulkSocialPostsRequest>,
 options?: SecondParameter<typeof customInstance<SocialBulkResult>>,) => {
      return customInstance<SocialBulkResult>(
      {url: `/admin/v1/stores/${storeId}/social/posts/bulk`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: bulkSocialPostsRequest
    },
      options);
    }
  /**
 * Uses the file method: Nucleus stores the network's verification file and serves it at GET /site-verification/{host}/{filename}; the storefront forwards /{filename} there. The API first fetches https://<domain>/<filename> itself, so a storefront without that rewrite ends as `website.status: failed`, `error: not_forwarded` instead of a network refusal. A domain the account already claimed is verified at once. Answers the connection with `website`. 409 SOCIAL_SCOPE_MISSING when the account was connected before the claim permission existed (reconnect with purpose connect), SOCIAL_WEBSITE_DOMAIN_MISSING without a primary domain, SOCIAL_NOT_CONNECTED / SOCIAL_CONNECTION_EXPIRED as elsewhere
 * @summary Claim the store's primary domain on the connected account (one click; run again to re-check)
 */
export const claimSocialWebsite = (
    { storeId, provider }: ClaimSocialWebsitePathParameters,
 options?: SecondParameter<typeof customInstance<SocialConnection>>,) => {
      return customInstance<SocialConnection>(
      {url: `/admin/v1/stores/${storeId}/social/${provider}/website/claim`, method: 'POST'
    },
      options);
    }
  export type ListSocialConnectionsResult = NonNullable<Awaited<ReturnType<typeof listSocialConnections>>>
export type StartSocialConnectResult = NonNullable<Awaited<ReturnType<typeof startSocialConnect>>>
export type DisconnectSocialResult = NonNullable<Awaited<ReturnType<typeof disconnectSocial>>>
export type GetSocialCatalogResult = NonNullable<Awaited<ReturnType<typeof getSocialCatalog>>>
export type EnableSocialCatalogResult = NonNullable<Awaited<ReturnType<typeof enableSocialCatalog>>>
export type DisableSocialCatalogResult = NonNullable<Awaited<ReturnType<typeof disableSocialCatalog>>>
export type IngestSocialCatalogResult = NonNullable<Awaited<ReturnType<typeof ingestSocialCatalog>>>
export type StartSocialAdsConnectResult = NonNullable<Awaited<ReturnType<typeof startSocialAdsConnect>>>
export type ListSocialAdAccountsResult = NonNullable<Awaited<ReturnType<typeof listSocialAdAccounts>>>
export type EnableSocialAdsResult = NonNullable<Awaited<ReturnType<typeof enableSocialAds>>>
export type DisableSocialAdsResult = NonNullable<Awaited<ReturnType<typeof disableSocialAds>>>
export type GetSocialAnalyticsOverviewResult = NonNullable<Awaited<ReturnType<typeof getSocialAnalyticsOverview>>>
export type ListSocialCampaignAnalyticsResult = NonNullable<Awaited<ReturnType<typeof listSocialCampaignAnalytics>>>
export type ListSocialTargetsResult = NonNullable<Awaited<ReturnType<typeof listSocialTargets>>>
export type ListSocialPostsResult = NonNullable<Awaited<ReturnType<typeof listSocialPosts>>>
export type CreateSocialPostResult = NonNullable<Awaited<ReturnType<typeof createSocialPost>>>
export type GetSocialPostResult = NonNullable<Awaited<ReturnType<typeof getSocialPost>>>
export type UpdateSocialPostResult = NonNullable<Awaited<ReturnType<typeof updateSocialPost>>>
export type RemoveSocialPostResult = NonNullable<Awaited<ReturnType<typeof removeSocialPost>>>
export type PublishSocialPostResult = NonNullable<Awaited<ReturnType<typeof publishSocialPost>>>
export type UnscheduleSocialPostResult = NonNullable<Awaited<ReturnType<typeof unscheduleSocialPost>>>
export type BulkSocialPostsResult = NonNullable<Awaited<ReturnType<typeof bulkSocialPosts>>>
export type ClaimSocialWebsiteResult = NonNullable<Awaited<ReturnType<typeof claimSocialWebsite>>>
